import { ICrossTherapyArticleLine, IPatient } from '@alberta/konexi-shared';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { ModalController, Platform } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { SelectModalComponent } from 'src/app/shared/components/common/select-modal/select-modal.component';
import { AuditNodeDto } from 'src/app/shared/models/swodoc/audit-node-dto.model';

import { PatientDB, UsersDB } from './../../../../../../common/repository/databases';
import { IIntegratedTherapyTypeInfo } from './../../../../../../patient/components/patient-detail/sections/integratedcare/integratedcare-therapy-type-info';
import { SelectIntegratedCareComponent } from './../../../../../../patient/components/patient-detail/sections/integratedcare/select-integrated-care/select-integrated-care.component';
import { UserDto } from './../../../../../models/user-management/user-dto.model';
import { AuthService } from './../../../../../services/auth.service';
import { QueryService } from './../../../../../services/query/query.service';
import { AddCrossTherapyArticleComponent } from './add-cross-therapy-article/add-cross-therapy-article.component';

@Component({
  selector: 'swodoc-node-cross-therapy-article',
  templateUrl: './swodoc-node-cross-therapy-article.component.html',
  styleUrls: ['./swodoc-node-cross-therapy-article.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SwodocNodeCrossTherapyArticle implements OnInit {
  @Input() node: AuditNodeDto;
  public integratedTherapyTypeInfos: IIntegratedTherapyTypeInfo[] = [];
  public currentUser: UserDto;
  public patient: IPatient;
  public showIdentificationNumbers = true;
  public showScanButtons = false;

  constructor(
    private _modalController: ModalController,
    private _changeDetectorRef: ChangeDetectorRef,
    private _translate: TranslateService,
    private _authService: AuthService,
    private _queryService: QueryService,
    private _platform: Platform
  ) {}

  public async ngOnInit() {
    if (this._platform.is('hybrid')) {
      this.showScanButtons = true;
    }
    this.node.mandatory = true;
    const userId = await this._authService.getCurrentUserId();
    this.currentUser = await this._queryService.get<UserDto>(userId, UsersDB);

    const integratedCares = [...this.node.responses[0].integratedCares];
    if (integratedCares) {
      this.patient = await this._queryService.get<IPatient>(integratedCares[0].patientId, PatientDB);
      integratedCares.forEach(integratedCare => {
        const therapyTypeInfo = {
          integratedCare: integratedCare,
        } as IIntegratedTherapyTypeInfo;
        this.integratedTherapyTypeInfos.push(therapyTypeInfo);
      });
    }
    this.showIdentificationNumbers = !this.node.options?.hideIdentificationNumbers;
    this._changeDetectorRef.markForCheck();
  }

  public async addArticle() {
    if (!this.node.options?.allArticles) {
      const selectedIntegratedCares = await this._modalController.create({
        component: SelectModalComponent,
        backdropDismiss: false,
        cssClass: ['selectModal', 'fullpageModal'],
        componentProps: {
          _content: SelectIntegratedCareComponent,
          header: this._translate.instant('crossTherapy.document.selectIntegratedCare'),
          buttonText: this._translate.instant('ok'),
          _componentProps: { therapyTypeInfos: this.integratedTherapyTypeInfos, maxAmount: 1 },
        },
      });
      selectedIntegratedCares.present();
      const selectedIntegratedCare = await selectedIntegratedCares.onDidDismiss();
      if (selectedIntegratedCare.data) {
        const addArticleModal = await this._modalController.create({
          component: AddCrossTherapyArticleComponent,
          backdropDismiss: false,
          componentProps: {
            disabled: true,
            therapyId: selectedIntegratedCare.data[0].therapyId,
            therapyTypeId: selectedIntegratedCare.data[0].therapyTypeId,
            user: this.currentUser,
            patient: this.patient,
            showScanButtons: this.showScanButtons,
            showIdentificationNumbers: this.showIdentificationNumbers,
            showStorageLocation: this.node.options?.showStorageLocation,
            articleFromDocument: true,
          },
          cssClass: 'fullPageModal',
        });
        await addArticleModal.present();
        const response = await addArticleModal.onDidDismiss();

        if (response.data) {
          this.saveArticles(response.data);
        }
      }
    } else {
      const addArticleModal = await this._modalController.create({
        component: AddCrossTherapyArticleComponent,
        backdropDismiss: false,
        componentProps: {
          showScanButtons: this.showScanButtons,
          user: this.currentUser,
          patient: this.patient,
          showStorageLocation: this.node.options?.showStorageLocation,
          showIdentificationNumbers: this.showIdentificationNumbers,
          articleFromDocument: true,
        },
        cssClass: 'fullPageModal',
      });
      await addArticleModal.present();
      const response = await addArticleModal.onDidDismiss();

      if (response.data) {
        this.saveArticles(response.data);
      }
    }
  }
  private saveArticles(articleLine: ICrossTherapyArticleLine) {
    const articleLines = [...this.node.responses[0].value];
    articleLines.push(articleLine);
    this.node.responses[0].value = articleLines as any;
    this._changeDetectorRef.markForCheck();
  }
}
